import { WithBase, WithRef } from "@converge-collective/common/models/Base";
import {
  Event,
  EventCategoryInfo,
  EventRaw,
  EventVisibilities,
} from "@converge-collective/common/models/Event";
import {
  EventActivityLog,
  EventLogKinds,
} from "@converge-collective/common/models/EventLog";
import { LiteGroup, Group } from "@converge-collective/common/models/Group";
import { parseGoogleAddressComponents } from "@converge-collective/common/models/Location";
import { Network } from "@converge-collective/common/models/Network";
import {
  EventCreatedPostRaw,
  POST_TYPE,
} from "@converge-collective/common/models/Post";
import { liteProfile } from "@converge-collective/common/models/Profile";
import { SubCategoryWithCategory } from "@converge-collective/common/models/SubCategory";
import { formatDate } from "@converge-collective/common/util";
import { Event as EventIcon } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  AlertTitle,
  Box,
  Dialog,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Switch,
  TextField,
  Theme,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { DateTimePicker } from "@mui/x-date-pickers";
import { addHours } from "date-fns";
import { isEmpty, kebabCase, merge, take } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { useFirestore, useUser } from "reactfire";
import AutocompleteGroup from "~/components/AutocompleteGroup";
import AutocompleteLocation from "~/components/AutocompleteLocation";
import Uploader, { baseUploadPath } from "~/components/Uploader";
import useProfile from "~/hooks/useProfile";
import AutocompleteCategory from "../AutocompleteCategory";
import TipTapEditor from "../editor/TipTapEditor";
import EventMap from "./EventMap";
import LockIcon from "@mui/icons-material/Lock";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { createAlert } from "~/lib/globalAlerts";

type AutocompletePrediction = google.maps.places.AutocompletePrediction;
type PlaceResult = google.maps.places.PlaceResult;

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 18,
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(4),
  },
  input: {
    // marginRight: 12,
  },
  placePhoto: {
    // maxHeight: 200,
    // maxWidth: "100%",
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "50% 50%",
  },
}));

const coeff = 1000 * 60 * 60;
const getStartAt = () =>
  new Date(Math.ceil(new Date().getTime() / coeff) * coeff);
const getEndAt = () => addHours(getStartAt(), 1);

type Props = {
  network: WithRef<Network>;
  isOpen: boolean;
  handleClose: () => void;
  handleEventCreated: (
    e: WithBase<Event>,
    eventCreatedPost: EventCreatedPostRaw
  ) => void;
  initGroups?: WithRef<Group>[];
};
export default function CreateEvent({
  isOpen,
  handleClose,
  handleEventCreated,
  network,
  initGroups,
}: Props): React.ReactElement {
  const classes = useStyles();
  const firestore = useFirestore();
  const [startAt, setStartAt] = useState(getStartAt());
  const [endAt, setEndAt] = useState(getEndAt());
  const [photoUrls, setPhotoUrls] = useState<string[]>();
  const [description, setDescription] = useState("");
  const [title, setTitle] = useState("");
  const [cost, setCost] = useState("");
  const [activityUrl, setActivityUrl] = useState("");
  const [, setLocation] = useState<AutocompletePrediction | null>(null);
  const [placeDetails, setPlaceDetails] = useState<PlaceResult | null>(null);
  // can be used for free form location or virtual location, like a zoom link
  const [locationDescription, setLocationDescription] = useState<string>();
  const [subCategory, setSubCategory] =
    useState<SubCategoryWithCategory | null>(null);
  const [groups, setGroups] = useState<LiteGroup[] | undefined>(initGroups);

  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // private is only set to true by default if the initi
  const [isPrivate, setIsPrivate] = useState(
    !isEmpty(initGroups?.[0]?.joinCode)
  );
  const visibleByIds = groups?.map((g) => g.id);

  const { data: user } = useUser();
  const [profile] = useProfile(user?.uid);
  const loading = false;

  // if we keep user id in the path we can create more granular permissions
  const uploadPath = `${baseUploadPath}/events/${user?.uid}`;

  const resetForm = useCallback(() => {
    console.log("resetForm");
    setTitle("");
    setDescription("");
    setStartAt(getStartAt());
    setEndAt(getEndAt());
    setLocation(null);
    setPlaceDetails(null);
    setSubCategory(null);
    setPhotoUrls([]);
    setGroups(initGroups);
  }, [initGroups]);

  // reset the form every time the modal is opened
  useEffect(() => {
    resetForm();
  }, [isOpen, resetForm]);

  const localHandlClose = () => {
    resetForm();
    handleClose();
  };

  const geometry = placeDetails?.geometry && {
    location: placeDetails.geometry.location?.toJSON(),
    viewport: placeDetails.geometry.viewport?.toJSON(),
  };

  const validate = () => {
    const errs = [
      ...(isEmpty(description) ? ["Description"] : []),
      ...(isEmpty(title) ? ["Title"] : []),
      ...(isEmpty(subCategory) ? ["Category"] : []),
      ...(!startAt ? ["Start at"] : []),
      ...(startAt && endAt && startAt > endAt
        ? ["Start at must be before End at date"]
        : []),
      ...(startAt && endAt && startAt < new Date()
        ? ["Start at must be in the future"]
        : []),
      // allow endAt to be optional
    ];
    console.log({ startAt, endAt });
    setValidationErrors(errs);
    return errs;
  };

  const [photosUploading, setPhotosUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const errs = validate();
    if (profile && subCategory && isEmpty(errs)) {
      const addressParts =
        placeDetails?.address_components &&
        parseGoogleAddressComponents(placeDetails.address_components);

      const providers: string[] = placeDetails?.name ? [placeDetails.name] : [];

      const streetNumber = addressParts?.["street_number"];
      const route = addressParts?.["route"];

      // might be undefined
      const address = streetNumber && route && `${streetNumber} ${route}`;
      if (!address) {
        console.info(
          "address is missing, which is expected if the user didn't enter one",
          {
            address,
            streetNumber,
            route,
            addressParts,
            placeDetails,
          }
        );
      }
      const city = addressParts?.["locality"];
      const state = addressParts?.["administrative_area_level_1"];
      const formattedAddress = placeDetails?.formatted_address || "";

      const categoryInfo: EventCategoryInfo = {
        categoryId: subCategory.category.id,
        categoryName: subCategory.category.name,
        subCategoryId: subCategory.subCategory.id,
        subCategoryName: subCategory.subCategory.subCategory,
      };

      const googlePlaceId = placeDetails?.place_id;
      const unresolvedPhotoUrls = placeDetails?.photos?.map((p) => p.getUrl());

      const event: EventRaw = merge(
        {
          isExperience: false,
          isEnabled: true,
          createdBy: liteProfile(profile),
          title,
          // TODO check uniqueness? the date makes it fairly unique but could
          // still overlap
          slug: kebabCase(`${title}-${formatDate(startAt)}`),
          description,
          providers,
          state: state || "",
          formattedAddress: formattedAddress || "",
          rrules: [],
          photoUrls: photoUrls || [],
          startAt,
          endAt,
          categoryInfo,
          visibility: isPrivate
            ? EventVisibilities.Private
            : EventVisibilities.Public,
        },
        // NOTE - we don't get type checking when adding fields via merge :/
        // maybe use {...{}} spread syntax instead
        address && { address },
        city && { city },
        googlePlaceId && { googlePlaceId },
        unresolvedPhotoUrls && { unresolvedPhotoUrls },
        geometry && { geometry },
        // only use the raw input as locationDescription if the user didn't set
        // a google place id and geometry
        locationDescription &&
          !googlePlaceId &&
          !geometry && { locationDescription },
        !isEmpty(cost) && { cost },
        !isEmpty(activityUrl) && { activityUrl },
        isPrivate && !isEmpty(visibleByIds) && { visibleByIds }
      );
      console.log("create event", { event, isPrivate, visibleByIds });

      const eventRef = await network.ref.collection("events").add(event);

      // create the log for event created
      const eventLog: EventActivityLog = {
        kind: EventLogKinds.Activity,
        date: new Date(),
        profile: liteProfile(profile),
        body: `${profile.name} created the event`,
      };
      console.log({ eventLog });
      eventRef.collection("logs").add(eventLog);

      const e: WithRef<WithBase<Event>> = {
        ...event,
        id: eventRef.id,
        ref: eventRef,
        createdAt: new Date(),
      };

      // automatically subscribe the creator of the event to the event chat
      await eventRef.collection("subscriptions").doc(profile.id).set({
        uid: profile.id,
      });

      // create the post
      const eventCreatedPost: EventCreatedPostRaw = {
        eventType: POST_TYPE.EventCreated,
        event: e,
        title: `${profile.name} created a new event: ${title}`,
        description,
        // this should be the time the event was created - not when the event
        // actually starts
        date: new Date(),
        avatar: profile.photoURL || "",
        profile: liteProfile(profile),
      };

      // only post it on the main feed if it's public
      if (event.visibility === EventVisibilities.Public) {
        await network.ref.collection("posts").add(eventCreatedPost);
      }

      // if the user specified groups to post the event in, do that here:
      if (groups && !isEmpty(groups)) {
        await Promise.all(
          groups.map((g) =>
            g.ref.collection("posts").doc(e.id).set(eventCreatedPost)
          )
        );
      }

      createAlert({
        message: `Event ${title} created`,
        severity: "success",
      });

      handleEventCreated(e, eventCreatedPost);
      localHandlClose();
    } else {
      profile ||
        console.warn(
          "profile was expected to be defined when submitting an event",
          { profile }
        );
      console.info("validation errors", { errs });
    }

    setIsSubmitting(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSubmit();
  };

  return (
    <>
      <Dialog
        maxWidth="md"
        className={classes.root}
        open={isOpen}
        onClose={localHandlClose}
        closeAfterTransition
      >
        <Paper className={classes.paper}>
          <Stack direction="row" alignItems="center" sx={{ mb: 4 }}>
            {isPrivate ? <LockIcon sx={{ mr: 1 }} /> : null}
            <Typography alignItems="center" variant="h4">
              Create {isPrivate ? " Private " : "Public "} Event
            </Typography>
          </Stack>

          {!isEmpty(initGroups) ? (
            <Alert
              action={
                <FormControlLabel
                  control={
                    <Switch
                      value={isPrivate}
                      onChange={(e) => setIsPrivate(e.target.checked)}
                      defaultChecked={isPrivate}
                    />
                  }
                  label="Private"
                />
              }
              sx={{ mb: 2 }}
              icon={isPrivate ? <LockIcon /> : <TaskAltIcon />}
              severity="info"
            >
              <AlertTitle>
                You are creating a {isPrivate ? " private " : "public "} event.
              </AlertTitle>
              {isPrivate ? (
                <>
                  It will only be visible in the
                  {(initGroups?.length || 0) > 1
                    ? " groups you selected"
                    : " group you selected"}
                  {": "}
                  {initGroups?.map((g) => (
                    <b key={g.id}>{g.name}</b>
                  ))}
                </>
              ) : (
                <>
                  It will be publicly accessible on the events page and
                  newsfeed.
                </>
              )}
            </Alert>
          ) : null}

          {!loading && (
            <Grid container spacing={2}>
              <Grid xs={12} sm={12} md={6} item>
                <TextField
                  onKeyPress={handleKeyPress}
                  value={title}
                  fullWidth
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setTitle(event.target.value);
                  }}
                  error={isEmpty(title) && !isEmpty(validationErrors)}
                  required
                  className={classes.input}
                  label="Title"
                />
              </Grid>

              <Grid item xs={12} sm={12} md={6}>
                <AutocompleteCategory
                  onSetSubCategories={(subCats: SubCategoryWithCategory[]) => {
                    if (!isEmpty(subCats)) {
                      setSubCategory(subCats[0]);
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={12}>
                <TipTapEditor
                  placeholder="Description *"
                  valueHtml={description}
                  onChange={(htmlDescription) => {
                    console.log("set description", { htmlDescription });
                    setDescription(htmlDescription);
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={6}>
                <DateTimePicker
                  label="Start at"
                  slotProps={{
                    textField: {
                      required: true,
                      onKeyPress: handleKeyPress,
                      fullWidth: true,
                    },
                  }}
                  minutesStep={15}
                  minDateTime={new Date()}
                  className={classes.input}
                  value={startAt}
                  onChange={(d: Date) => {
                    setStartAt(d);
                    setEndAt(addHours(d, 1));
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <DateTimePicker
                  label="End at"
                  slotProps={{
                    textField: {
                      onKeyPress: handleKeyPress,
                      fullWidth: true,
                    },
                  }}
                  minutesStep={15}
                  minDateTime={new Date()}
                  className={classes.input}
                  value={endAt}
                  onChange={(d: Date) => setEndAt(d)}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ my: 2 }}>
                  <Uploader
                    uploadPath={uploadPath}
                    onPhotosSelected={() => setPhotosUploading(true)}
                    onPhotosUploaded={({ photoUrls }) => {
                      console.log("photos uploaded!!", { photoUrls });
                      setPhotosUploading(false);
                      setPhotoUrls(photoUrls);
                    }}
                    uploadButtonText="Add Event Photos"
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={12} sx={{ mt: 1 }}>
                <AutocompleteGroup
                  initSelected={initGroups}
                  onSetGroups={(gs) => setGroups(gs)}
                  multiple={true}
                  required={false}
                  helperText="Optional: Groups to post the event in - must be a group member"
                />
              </Grid>

              <Grid item xs={12} sm={12} sx={{ mt: 1 }}>
                <AutocompleteLocation
                  onLocationSelected={(l) => setLocation(l)}
                  onRawInput={(locDesc) => setLocationDescription(locDesc)}
                  onPlaceDetails={(p) => setPlaceDetails(p)}
                />
              </Grid>

              <Grid item xs={12} sm={12}>
                <Grid
                  container
                  spacing={2}
                  style={{ marginTop: 4, marginBottom: 4 }}
                >
                  {placeDetails?.photos &&
                    take(placeDetails.photos, 4).map((p, idx) => (
                      <Grid item sm={3} key={idx}>
                        <img
                          alt={placeDetails?.name}
                          className={classes.placePhoto}
                          src={p.getUrl()}
                          key={idx}
                        />
                      </Grid>
                    ))}
                </Grid>

                {geometry && (
                  <EventMap
                    containerStyles={{
                      marginTop: 12,
                    }}
                    mapStyles={{
                      height: 200,
                      borderRadius: 0,
                    }}
                    geometry={geometry}
                  />
                )}
              </Grid>

              <Grid xs={12} sm={12} md={6} item>
                <TextField
                  onKeyPress={handleKeyPress}
                  value={cost}
                  fullWidth
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setCost(event.target.value);
                  }}
                  className={classes.input}
                  helperText="Optional"
                  label="Cost"
                />
              </Grid>

              <Grid item xs={12} sm={12} md={6}>
                <TextField
                  onKeyPress={handleKeyPress}
                  value={activityUrl}
                  fullWidth
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setActivityUrl(event.target.value);
                  }}
                  className={classes.input}
                  helperText="Optional"
                  label="Registration URL"
                />
              </Grid>

              <Grid item xs={12} sm={12}>
                {validationErrors.length > 0 && (
                  <Alert severity="error">
                    <AlertTitle>
                      Please enter all required fields order to create an event:
                    </AlertTitle>

                    <ul>
                      {validationErrors.map((v) => (
                        <li key={v}>{v}</li>
                      ))}
                    </ul>
                  </Alert>
                )}
                <Box display="flex" mt={2} justifyContent="flex-end">
                  <LoadingButton
                    startIcon={<EventIcon />}
                    loading={isSubmitting || photosUploading}
                    onClick={handleSubmit}
                    style={{ marginTop: 10, marginLeft: 12 }}
                    color="primary"
                    variant="contained"
                  >
                    Create Event
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
          )}
        </Paper>
      </Dialog>
    </>
  );
}
